import React, { useEffect, lazy, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/admin/ProtectedRoute';
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { Toaster } from "./components/ui/sonner";
import { FloatingWhatsApp } from "./components/FloatingWhatsApp";
import { StickyCallButton } from "./components/StickyCallButton";
import { CookieConsent } from "./components/CookieConsent";
import ErrorBoundary from "./components/ErrorBoundary";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import PWAUpdateNotification from "./components/PWAUpdateNotification";
import PushNotificationManager from "./components/PushNotificationManager";

// Lazy load pages for better performance
const Home = lazy(() => import("./pages/Home").then(module => ({ default: module.Home })));
const Hakkimizda = lazy(() => import("./pages/Hakkimizda").then(module => ({ default: module.Hakkimizda })));
const Sirketlerimiz = lazy(() => import("./pages/Sirketlerimiz").then(module => ({ default: module.Sirketlerimiz })));
const OrtadoguSeed = lazy(() => import("./pages/OrtadoguSeed").then(module => ({ default: module.OrtadoguSeed })));
const AkovaTarim = lazy(() => import("./pages/AkovaTarim").then(module => ({ default: module.AkovaTarim })));
const AkovaPamuk = lazy(() => import("./pages/AkovaPamuk").then(module => ({ default: module.AkovaPamuk })));
const Tohumculuk = lazy(() => import("./pages/Tohumculuk").then(module => ({ default: module.Tohumculuk })));
const TohumDetay = lazy(() => import("./pages/Tohumculuk").then(module => ({ default: module.TohumDetay })));
const ProductDetail = lazy(() => import("./pages/ProductDetail").then(module => ({ default: module.ProductDetail })));
const Urunler = lazy(() => import("./pages/Urunler").then(module => ({ default: module.Urunler })));
const Iletisim = lazy(() => import("./pages/Iletisim").then(module => ({ default: module.Iletisim })));
const GizlilikPolitikasi = lazy(() => import("./pages/GizlilikPolitikasi").then(module => ({ default: module.GizlilikPolitikasi })));
const KullanimSartlari = lazy(() => import("./pages/KullanimSartlari").then(module => ({ default: module.KullanimSartlari })));
const Blog = lazy(() => import("./pages/Blog"));
const BlogDetail = lazy(() => import("./pages/BlogDetail"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Certificates = lazy(() => import("./pages/Certificates"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Admin pages
const AdminLogin = lazy(() => import("./pages/admin/Login"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminMesajlar = lazy(() => import("./pages/admin/Mesajlar"));
const AdminTeklifler = lazy(() => import("./pages/admin/Teklifler"));
const AdminBlogYonetimi = lazy(() => import("./pages/admin/BlogYonetimi"));
const AdminSSSYonetimi = lazy(() => import("./pages/admin/SSSYonetimi"));
const AdminEmailGonder = lazy(() => import("./pages/admin/EmailGonder"));
const AdminPushBildirim = lazy(() => import("./pages/admin/PushBildirim"));

// Loading component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      <p className="mt-4 text-gray-600">Yükleniyor...</p>
    </div>
  </div>
);

// Scroll to top component
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <AuthProvider>
          <div className="App">
            <BrowserRouter>
              <ScrollToTop />
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Public routes with Header/Footer */}
                  <Route path="/" element={<><Header /><Home /><Footer /></>} />
                  <Route path="/hakkimizda" element={<><Header /><Hakkimizda /><Footer /></>} />
                  <Route path="/sirketlerimiz" element={<><Header /><Sirketlerimiz /><Footer /></>} />
                  <Route path="/sirketlerimiz/ortadogu-seed" element={<><Header /><OrtadoguSeed /><Footer /></>} />
                  <Route path="/sirketlerimiz/akova-tarim" element={<><Header /><AkovaTarim /><Footer /></>} />
                  <Route path="/sirketlerimiz/akova-pamuk" element={<><Header /><AkovaPamuk /><Footer /></>} />
                  <Route path="/tohumculuk" element={<><Header /><Tohumculuk /><Footer /></>} />
                  <Route path="/tohumculuk/:slug" element={<><Header /><TohumDetay /><Footer /></>} />
                  <Route path="/tohumculuk/:categorySlug/:productSlug" element={<><Header /><ProductDetail /><Footer /></>} />
                  {/* Old routes redirect to new */}
                  <Route path="/tohumculuk/ekmeklik-bugday/serche" element={<><Header /><ProductDetail /><Footer /></>} />
                  <Route path="/tohumculuk/makarnalik-bugday/apache" element={<><Header /><ProductDetail /><Footer /></>} />
                  <Route path="/urunler" element={<><Header /><Urunler /><Footer /></>} />
                  <Route path="/iletisim" element={<><Header /><Iletisim /><Footer /></>} />
                  <Route path="/blog" element={<><Header /><Blog /><Footer /></>} />
                  <Route path="/blog/:slug" element={<><Header /><BlogDetail /><Footer /></>} />
                  <Route path="/sss" element={<><Header /><FAQ /><Footer /></>} />
                  <Route path="/sertifikalar" element={<><Header /><Certificates /><Footer /></>} />
                  <Route path="/gizlilik" element={<><Header /><GizlilikPolitikasi /><Footer /></>} />
                  <Route path="/kullanim" element={<><Header /><KullanimSartlari /><Footer /></>} />

                  {/* Admin routes - NO Header/Footer */}
                  <Route path="/admin/login" element={<AdminLogin />} />
                  <Route path="/admin/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                  <Route path="/admin/mesajlar" element={<ProtectedRoute><AdminMesajlar /></ProtectedRoute>} />
                  <Route path="/admin/teklifler" element={<ProtectedRoute><AdminTeklifler /></ProtectedRoute>} />
                  <Route path="/admin/blog" element={<ProtectedRoute><AdminBlogYonetimi /></ProtectedRoute>} />
                  <Route path="/admin/sss" element={<ProtectedRoute><AdminSSSYonetimi /></ProtectedRoute>} />
                  <Route path="/admin/email" element={<ProtectedRoute><AdminEmailGonder /></ProtectedRoute>} />
                  <Route path="/admin/push-bildirim" element={<ProtectedRoute><AdminPushBildirim /></ProtectedRoute>} />

                  {/* 404 - Catch all */}
                  <Route path="*" element={<><Header /><NotFound /><Footer /></>} />
                </Routes>
              </Suspense>
              <Toaster />
              <FloatingWhatsApp />
              <StickyCallButton />
              <CookieConsent />
              <PWAInstallPrompt />
              <PWAUpdateNotification />
              <PushNotificationManager />
            </BrowserRouter>
          </div>
        </AuthProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
